import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { AppInventoryItem, AppVersion } from "@/types/inventory";

export const useAppInventory = () => {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['app-inventory'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_app_inventory');
      
      if (error) throw error;
      
      // Transform the data to match our TypeScript types
      const inventory: AppInventoryItem[] = (data || []).map((item: any) => {
        // Deduplicate versions by version string (safeguard against any residual duplicates)
        const versionMap = new Map<string, AppVersion>();
        const versions = Array.isArray(item.versions) ? item.versions as AppVersion[] : [];
        
        versions.forEach((v: AppVersion) => {
          const existing = versionMap.get(v.version);
          if (existing) {
            existing.device_count += v.device_count;
          } else {
            versionMap.set(v.version, { ...v });
          }
        });
        
        return {
          name: item.name,
          bundle_id: item.bundle_id,
          icon: item.icon,
          total_devices: item.total_devices,
          versions: Array.from(versionMap.values()).sort((a, b) => b.device_count - a.device_count),
          first_seen: item.first_seen,
          last_updated: item.last_updated,
          is_managed: item.is_managed,
        };
      });
      
      return inventory;
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  return {
    inventory: data || [],
    isLoading,
    error,
    refetch,
  };
};
